import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { formatDate } from '@angular/common';
import { Subscription } from 'rxjs';

import { Constants, ERROR_MESSAGES, PingParameter, SSO_SPLUNK, SsoApplicationType, SsoParameter } from '../../shared/constants/constants';
import { SsoRequestParameter } from './models/sso-request-parameter.model';
import { SsoService } from '../services/sso.service';
import { APP_CONFIG, AppConfigService } from '../services/app-config.service';
import { UserService } from '../services/user.service';
import { DropOffResponse } from '../../shared/models/drop-off-response.model';
import { MemberSearchService } from '../../modules/member/services/member-account-search.service';
import { BillingInfoRequestBody } from '../../modules/member/models/member-billing-info.request';
import { LoggerService } from '../../shared/services/logger.service';
import { WhyzenForm } from './models/whyzen-form.model';

@Component({
    selector: 'app-sso-redirection',
    templateUrl: './sso-redirection.component.html',
    styleUrls: ['./sso-redirection.component.scss']
})
export class SsoRedirectionComponent implements OnInit, OnDestroy {

  // Portal / Application Name
  applicationName = '';
  // Id on which spoof is done
  userIdentifierForAudit = '';
  // SSO Event Status
  eventStatus = '';
  // Spoof User Name
  bscUserName = '';
  employerUserName = '';
  rlBeta = '';

  // Array to store subscriptions for cleanup on component destruction
  subscriptions: Subscription[] = [];

  whyzenFormData = new WhyzenForm('', '', '', '');
  @ViewChild('whyzenForm') whyzenForm!: ElementRef;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private ssoService: SsoService,
    private loggerService: LoggerService,
    private user: UserService,
    private memberService: MemberSearchService,
    private appConfigService: AppConfigService
  ) { }

  /*
  * SSO redirection has the following steps:
  *
  * Step 1: Identifying the Application Type
  * Step 2: Creating request body for drop off call based on Application Type
  * Step 3: Calling Drop Off service
  * Step 4: Getting Reference ID from Drop off response
  * Step 5: Redirect to Ping with Reference ID and required SSO Parameters
  */

  // START - Step 1: Identifying the Application Type
  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe({
      next: (param: Params) => {
        this.applicationName = param[SsoParameter.SSO];
        this.rlBeta = param['rlBeta'];
        switch (this.applicationName) {
          case SsoApplicationType.MEMBER_PAYMENT_RESEARCH:
            this.memberPaymentSearchSso();
            break;
          case SsoApplicationType.WHYZEN:
            this.whyzenSso(param);
            break;
          case SsoApplicationType.ZIPARI:
            this.zipariSso();
            break;
          case SsoApplicationType.MEMBER:
            this.openMemberSso(param[SsoParameter.BSC_PROFILE_UID]);
            break;
          case SsoApplicationType.SELF_ADMIN:
            this.openSelfAdminSso(param[SsoParameter.SELF_ADMIN_USER_ID]);
            break;
          case SsoApplicationType.PROVIDER:
            this.openProviderSso(param[SsoParameter.PROVIDER_USER_ID]);
            break;
          case SsoApplicationType.PRODUCER:
            this.producerSso(param[SsoParameter.USER_NAME], param['groupId'], param['uid']);
            break;
          case SsoApplicationType.EMPLOYER:
            this.employerSso(param[SsoParameter.USER_NAME], param['spoofId'], param['groupId']);
            break;
          case SsoApplicationType.MEMBER_MAKE_A_PAYMENT:
            this.memberMakePayment(param[SsoParameter.SUBCRIBER_ID], param[SsoParameter.EXTERNAL_USERID], param[SsoParameter.FIRST_NAME], param[SsoParameter.LAST_NAME], param[SsoParameter.MIDDLE_NAME], param[SsoParameter.MAKE_A_PAYMENT_MAIL], param[SsoParameter.PRE_CUTOVER_PAGE_USER_FLAG], param[SsoParameter.SOURCE_SYSTEM]);
            break;
          default:
            this.redirectToHome();
            break;
        }
      }
    });
  }
  // END- Step 1: Identifying the Application Type

  // Creating request body for drop off call based on Application Type and Drop Off call
  memberPaymentSearchSso() {
    //  Step 2: Creating request body for drop off call based on Application Type
    const request = this.createMemberPaymentSearchSSORequestBody(this.user.getUserName);
    // Step 3: Calling Drop Off service
    this.callSSODropOff(request);
  }

  whyzenSso(param: Params) {
    // Step 2: Creating request body for drop off call based on Application Type
    const accessLevel = param['al'] || '';
    const isWhyzenAzureSso = APP_CONFIG.ENABLE_WHYZEN_AZURE_SSO === 'true';
    const configSuffix = isWhyzenAzureSso ? '_AZURE' : '';
    const whyzenSsoUrl = isWhyzenAzureSso ? APP_CONFIG.WHYZEN_SSO_URL_AZURE : APP_CONFIG.WHYZEN_SSO_URL;
    const isNonsalesAccess = accessLevel && (accessLevel === Constants.EXCLUDE_BSCGroup || accessLevel === Constants.INCLUDE_BSCGroup);
    let appId: string;
    let tenantId: string;

    if (isNonsalesAccess) {
      appId = APP_CONFIG['WHYZEN_SAASID_NONSALES' + configSuffix];
      tenantId = APP_CONFIG['WHYZEN_IDPID_NONSALES' + configSuffix];
    } else {
      appId = APP_CONFIG['WHYZEN_SAASID_SALES_' + accessLevel + configSuffix];
      tenantId = APP_CONFIG['WHYZEN_IDPID_SALES_' + accessLevel + configSuffix];
    }

    // Splunk
    this.userIdentifierForAudit = this.user.getUserName;
    this.eventStatus = Constants.SUCCESS;
    this.updateSplunk();

    if (isWhyzenAzureSso) {
      // Construct Entra URL: {baseUrl}{appId}?tenantId={tenantId}
      window.location.href = `${whyzenSsoUrl}${appId}?tenantId=${tenantId}`;
    } else {
      this.whyzenFormData = new WhyzenForm(whyzenSsoUrl, appId, tenantId, this.user.getUserName);
      setTimeout(() => this.whyzenForm.nativeElement.submit(), 100);
    }
  }

  zipariSso() {
    //Step 2: Creating request body for drop off call based on Application Type
    const request = this.createZiphariSSORequestBody();
    // Step 3: Calling Drop Off service
    this.callSSODropOff(request);
  }

  openMemberSso(bscProfileuid: string) {
    if (bscProfileuid) {
      //Step 2: Creating request body for drop off call based on Application Type
      const request = this.createMemberSSORequestBody(bscProfileuid);
      // Step 3: Calling Drop Off service
      this.callSSODropOff(request);
    } else {
      this.redirectToHome();
    }
  }

  openProviderSso(providerUserId: string) {
    if (providerUserId) {
      //Step 2: Creating request body for drop off call based on Application Type
      const request = this.createProviderSSORequestBody(this.user.getUserName, this.user.getUserName, providerUserId);
      // Step 3: Calling Drop Off service
      this.callSSODropOff(request);
    } else {
      this.redirectToHome();
    }
  }

  openSelfAdminSso(profileIdentifier: string) {
    if (profileIdentifier) {
      //Step 2: Creating request body for drop off call based on Application Type
      const request = this.createSelfAdminSSORequestBody(profileIdentifier);
      // Step 3: Calling Drop Off service
      this.callSSODropOff(request);
    } else {
      this.redirectToHome();
    }
  }

  producerSso(bscUsername: string, groupId: string = '', dsaUserId: string = '') {
    if (bscUsername) {
      //Step 2: Creating request body for drop off call based on Application Type
      const request = this.createProducerSSORequestBody(bscUsername, groupId, dsaUserId);
      // Step 3: Calling Drop Off service
      this.callSSODropOff(request);
    } else {
      this.redirectToHome();
    }
  }

  employerSso(username: string, spoofId: string, groupId: string = '') {
    if (username) {
      this.employerUserName = username;
      //Step 2: Creating request body for drop off call based on Application Type
      const request = this.createEmployerSSORequestBody(username, spoofId, groupId);
      // Step 3: Calling Drop Off service
      this.callSSODropOff(request);
    } else {
      this.redirectToHome();
    }
  }

  async memberMakePayment(subscriberId: string, externalUserId: string, firstName: string, lastName: string, middleName: string, emailAddress: string, preCutoverPageUserFlag: string, sourceSystem: string) {

    if (subscriberId) {
      //Step 2: Creating request body for drop off call based on Application Type
      const request = await this.createMemberMakePaymentSSORequestBody(subscriberId, externalUserId, firstName, lastName, middleName, emailAddress, preCutoverPageUserFlag, sourceSystem);
      // Step 3: Calling Drop Off service
      this.callSSODropOff(request, preCutoverPageUserFlag);
    } else {
      this.redirectToHome();
    }
  }
  // END - Step 2: Creating request body for drop off call based on Application Type


  // START - Create Request body Based on Application Type
  createMemberPaymentSearchSSORequestBody(username: string): any {
    this.userIdentifierForAudit = Constants.BSC + username;
    return {
      applicationName: SsoApplicationType.MEMBER_PAYMENT_RESEARCH,
      ssoRequestParameter: {
        ssoRequestParameters: [
          new SsoRequestParameter(SsoParameter.SUBJECT, username),
          new SsoRequestParameter(SsoParameter.USER_NAME, Constants.BSC + username)
        ]
      }
    };
  }

  createZiphariSSORequestBody(): any {
    this.userIdentifierForAudit = this.user.getUserIdentifier;
    return {
      applicationName: SsoApplicationType.ZIPARI,
      ssoRequestParameter: {
        ssoRequestParameters: [
          new SsoRequestParameter(SsoParameter.USER_NAME, this.user.getUserName),
          new SsoRequestParameter(SsoParameter.EMAIL_ADDRESS, this.user.getUserProfile.emailAddress),
          new SsoRequestParameter(SsoParameter.ROLES, Constants.ZIPARI_ADMIN),
          new SsoRequestParameter(SsoParameter.BROKER_ID, this.user.getUserIdentifier),
          new SsoRequestParameter(SsoParameter.SUBJECT, this.user.getUserIdentifier),
          new SsoRequestParameter(SsoParameter.USER_ID, this.user.getUserIdentifier),
          new SsoRequestParameter(SsoParameter.AGENCY_ID, this.user.getUserIdentifier)
        ]
      }
    };
  }

  createMemberSSORequestBody(bscProfileuid: string): any {
    this.userIdentifierForAudit = bscProfileuid;
    return {
      applicationName: SsoApplicationType.MEMBER,
      ssoRequestParameter: {
        ssoRequestParameters: [
          new SsoRequestParameter(SsoParameter.CSADMIN_USER_ID, this.user.getUserName),
          new SsoRequestParameter(SsoParameter.SUBJECT, this.user.getUserName),
          new SsoRequestParameter(SsoParameter.BSC_PROFILE_UID, bscProfileuid)
        ]
      }
    };
  }

  createProviderSSORequestBody(dpAdminUserName: string, subject: string, providerUserId: string): any {
    this.userIdentifierForAudit = providerUserId;
    return {
      applicationName: SsoApplicationType.PROVIDER,
      ssoRequestParameter: {
        ssoRequestParameters: [
          new SsoRequestParameter(SsoParameter.CSADMIN_USER_ID, dpAdminUserName),
          new SsoRequestParameter(SsoParameter.SUBJECT, subject),
          new SsoRequestParameter(SsoParameter.PROVIDER_USER_ID, providerUserId)
        ]
      }
    };
  }

  createSelfAdminSSORequestBody(profileIdentifier: string): any {
    this.userIdentifierForAudit = profileIdentifier;
    return {
      applicationName: SsoApplicationType.SELF_ADMIN,
      ssoRequestParameter: {
        ssoRequestParameters: [
          new SsoRequestParameter(SsoParameter.CSADMIN_USER_ID, this.user.getUserName),
          new SsoRequestParameter(SsoParameter.SELF_ADMIN_USER_ID, profileIdentifier),
          new SsoRequestParameter(SsoParameter.SUBJECT, this.user.getUserName)
        ]
      }
    };
  }

  createProducerSSORequestBody(bscUsername: string, groupId: string, dsaUserId: string): any {
    this.userIdentifierForAudit = bscUsername;
    this.bscUserName = bscUsername;
    return {
      applicationName: SsoApplicationType.PRODUCER,
      ssoRequestParameter: {
        ssoRequestParameters: [
          new SsoRequestParameter(SsoParameter.CSADMIN_USER_ID, dsaUserId || this.user.getUserIdentifier),
          new SsoRequestParameter(SsoParameter.BSC_USERNAME, bscUsername),
          new SsoRequestParameter(SsoParameter.SUBJECT, this.user.getUserName),
          groupId && new SsoRequestParameter(SsoParameter.SUBJECT, groupId)
        ].filter(Boolean)
      }
    };
  }

  createEmployerSSORequestBody(username: string, spoofId: string, groupId: string): any {
    this.userIdentifierForAudit = spoofId;
    this.bscUserName = username;
    return {
      applicationName: SsoApplicationType.EMPLOYER,
      ssoRequestParameter: {
        ssoRequestParameters: [
          new SsoRequestParameter(SsoParameter.CSADMIN_USER_ID, username),
          new SsoRequestParameter(SsoParameter.EMPLOYER_USERID, spoofId),
          new SsoRequestParameter(SsoParameter.SUBJECT, this.user.getUserName),
          groupId && new SsoRequestParameter(SsoParameter.GROUP_ID, groupId)
        ].filter(Boolean)
      }
    };
  }

  async createMemberMakePaymentSSORequestBody(subscriberId: string, externalUserId: string, firstName: string, lastName: string, middleName: string, emailAddress: string, preCutoverPageUserFlag: string, sourceSystem: string): Promise<any> {
    this.userIdentifierForAudit = subscriberId;
    let billingInfoResponse: any = null;
    const isPreCutoverPageUser: boolean = (preCutoverPageUserFlag === Constants.Y_FLAG);
    const obj: any = {};
    try {
      if (!isPreCutoverPageUser) {
        const billingRequestBody: BillingInfoRequestBody = {
          startDate: null,
          endDate: null,
          customer: {
            id: (externalUserId) ? externalUserId : "",
            idType: Constants.EXTU_ID,
            type: Constants.SUBSCRIBER
          },
          producer: {
            id: null
          },
          billing: {
            accountStatus: null
          }
        };
        const billService = new Promise((resolve: any, reject: any) => {
          this.memberService.getBillingInfo(billingRequestBody).subscribe({
            next: (response: any) => { resolve(response) },
            error: (err: any) => reject(err)
          })
        })
        billingInfoResponse = await billService;
        if (billingInfoResponse !== null && billingInfoResponse.getBillingInfoResponse != null) {
          billingInfoResponse = billingInfoResponse.getBillingInfoResponse;
        }
      }

      if (isPreCutoverPageUser || billingInfoResponse != null) {
        let locale = navigator.language.toString();
        if (locale != null && locale.includes("_")) {
          locale = locale.replace("_", "-");
        } else {
          locale = Constants.EN_US;
        }

        obj.First_Name = firstName;
        obj.Last_Name = (lastName) ? lastName : '';
        obj.Middle_Name = (middleName) ? middleName : '';
        obj.Supplemental_Id = subscriberId;
        obj.User_Id = this.user.getUserIdentifier;
        obj.subject = this.user.getUserIdentifier;
        obj.User_Type = Constants.CSA;
        obj.User_Role = Constants.ZIPARI_ADMIN;
        obj.Email = (emailAddress) ? emailAddress : '';
        obj.Source_System = sourceSystem;
        obj.Language = locale;
        if (billingInfoResponse && billingInfoResponse.responseBody != null
          && billingInfoResponse.responseBody.customer != null &&
          billingInfoResponse.responseBody.customer[0] && billingInfoResponse.responseBody.customer[0].billing &&
          billingInfoResponse.responseBody.customer[0].billing[0]) {
          if (billingInfoResponse.responseBody.customer[0].billing[0].currentBalance != null &&
            billingInfoResponse.responseBody.customer[0].billing[0].currentBalance > 0) {
            obj.Premium_Amount = billingInfoResponse.responseBody.customer[0].billing[0].currentBalance.toString();
          } else {
            // https://jira.bsc.bscal.com/browse/CSA-288
            obj.Premium_Amount = "0.0";
          }
          if (billingInfoResponse.responseBody.customer[0].billing[0].billDueDate != null) {
            obj.Premium_Due_Date = formatDate(billingInfoResponse.responseBody.customer[0].billing[0].billDueDate, Constants.MM_DD_YYYY, Constants.EN_US);
          }
          if (billingInfoResponse.responseBody.customer[0].billing[0].billCoverageStartDate != null) {
            obj.Billing_Period_Start_Date = formatDate(billingInfoResponse.responseBody.customer[0].billing[0].billCoverageStartDate, Constants.MM_DD_YYYY, Constants.EN_US);
          }
          if (billingInfoResponse.responseBody.customer[0].billing[0].billCoverageToDate != null) {
            obj.Billing_Period_End_Date = formatDate(billingInfoResponse.responseBody.customer[0].billing[0].billCoverageToDate, Constants.MM_DD_YYYY, Constants.EN_US);
          }

        }
        if (billingInfoResponse && billingInfoResponse.responseBody != null
          && billingInfoResponse.responseBody.customer != null &&
          billingInfoResponse.responseBody.customer[0] &&
          billingInfoResponse.responseBody.customer[0].deferredPaymentOpted != null) {
          obj.Deferred_Flag = billingInfoResponse.responseBody.customer[0].deferredPaymentOpted;
        } else {
          obj.Deferred_Flag = Constants.N_FLAG;
        }
      } else {

        throw new Error(ERROR_MESSAGES.BILLING_RESPONSE_NOT_AVAILABLE);
      }
    } catch (e) {
      throw new Error(ERROR_MESSAGES.BILLING_RESPONSE_NOT_AVAILABLE);
    }
    const ssoRequestParameters = [
      new SsoRequestParameter(SsoParameter.SUPPLEMENTAL_ID, obj.Supplemental_Id),
      new SsoRequestParameter(SsoParameter.MAKE_A_PAYMENT_USER_ID, this.user.getUserIdentifier),
      new SsoRequestParameter(SsoParameter.SUBJECT, this.user.getUserIdentifier),
      new SsoRequestParameter(SsoParameter.USER_TYPE, obj.User_Type),
      new SsoRequestParameter(SsoParameter.USER_ROLE, obj.User_Role),
      new SsoRequestParameter(SsoParameter.MAKE_A_PAYMENT_FIRST_NAME, obj.First_Name),
      new SsoRequestParameter(SsoParameter.DEFERRED_FLAG, obj.Deferred_Flag),
      new SsoRequestParameter(SsoParameter.MAKE_A_PAYMENT_MIDDLE_NAME, obj.Middle_Name),
      new SsoRequestParameter(SsoParameter.MAKE_A_PAYMENT_LAST_NAME, obj.Last_Name),
      new SsoRequestParameter(SsoParameter.MAKE_A_PAYMENT_EMAIL, obj.Email),
      new SsoRequestParameter(SsoParameter.MAKE_A_PAYMENT_SOURCE_SYSTEM, obj.Source_System),
      new SsoRequestParameter(SsoParameter.LANGUAGE, obj.Language)
    ]
    if (obj.Premium_Amount) {
      ssoRequestParameters.push(new SsoRequestParameter(SsoParameter.PREMIUM_AMOUNT, obj.Premium_Amount))
    }
    if (obj.Premium_Due_Date) {
      ssoRequestParameters.push(new SsoRequestParameter(SsoParameter.PREMIUM_DUE_DATE, obj.Premium_Due_Date))
    }
    if (obj.Billing_Period_Start_Date) {
      ssoRequestParameters.push(new SsoRequestParameter(SsoParameter.BILLING_PERIOD_START_DATE, obj.Billing_Period_Start_Date))
    }
    if (obj.Billing_Period_End_Date) {
      ssoRequestParameters.push(new SsoRequestParameter(SsoParameter.BILLING_PERIOD_END_DATE, obj.Billing_Period_End_Date))
    }
    return {
      applicationName: SsoApplicationType.MEMBER_MAKE_A_PAYMENT,
      ssoRequestParameter: {
        ssoRequestParameters: ssoRequestParameters
      }
    };
  }

  // END - Create Request body BAsed on Application Type

  // Step 3: Calling Drop Off service
  callSSODropOff(request: any, preCutoverPageUserFlag = 'N') {
    this.subscriptions.push(
      this.ssoService.getSSOReferenceId(request)
        .subscribe({
          next: (response: DropOffResponse) => {
            // Step 4: Getting Reference ID from Drop off response
            if (response.responseHeader.transactionNotification.status === Constants.SUCCESS) {
              const refId = response.responseBody.referenceIdentifier;
              this.prepareForPingRedirection(refId, preCutoverPageUserFlag);
              this.eventStatus = Constants.SUCCESS;
              this.updateSplunk();
            } else {
              this.eventStatus = Constants.FAILURE;
              this.updateSplunk();
              this.redirectToHome();
            }
          },
          error: () => {
            this.eventStatus = Constants.FAILURE;
            this.updateSplunk();
            this.redirectToHome();
          }
        })
    );
  }

  // START - Step 5: Redirect to Ping with Reference ID and required SSO Parameters along with killswitch logic
  prepareForPingRedirection(refID: string, preCutoverPageUserFlag: string) {
    switch (this.applicationName) {
      case SsoApplicationType.MEMBER_PAYMENT_RESEARCH:
        this.redirectToMemberPaymentResearch(refID);
        break;
      case SsoApplicationType.WHYZEN:
        this.redirectToPing(refID, '');
        break;
      case SsoApplicationType.ZIPARI:
        this.redirectToPing(refID, APP_CONFIG.ZIPARI_PARTNER_ID);
        break;
      case SsoApplicationType.MEMBER:
        this.redirectToPing(refID, APP_CONFIG.MEMBER_PARTNER_ID);
        break;
      case SsoApplicationType.SELF_ADMIN: {
        const selfAdminPartnerId = this.isTpaCloudActive()
          ? (APP_CONFIG.SELF_ADMIN_PARTNER_ID_CLOUD || APP_CONFIG.SELF_ADMIN_PARTNER_ID)
          : APP_CONFIG.SELF_ADMIN_PARTNER_ID;
        this.redirectToPing(refID, selfAdminPartnerId);
        break;
      }
      case SsoApplicationType.PROVIDER:
        this.redirectToPing(refID, APP_CONFIG.PROVIDER_PARTNER_ID);
        break;
      case SsoApplicationType.PRODUCER:
        this.redirectToPing(refID, APP_CONFIG.PRODUCER_PARTNER_ID);
        break;
      case SsoApplicationType.EMPLOYER:
        this.redirectToPing(refID, this.rlBeta === 'RLBETA' ? APP_CONFIG.EMPLOYER_PARTNER_ID : APP_CONFIG.EMPLOYER_PARTNER_ID_NRL);
        break;
      case SsoApplicationType.MEMBER_MAKE_A_PAYMENT:
        this.redirectToMemberMakePayment(refID, preCutoverPageUserFlag);
        break;
      default:
        this.redirectToHome();
        break;
    }
  }

  private isTpaCloudActive(): boolean {
    const tpaKillswitchValue = (this.appConfigService.tpaKillswitchValue || 'APIC').trim().toUpperCase();
    return tpaKillswitchValue === 'CENTRALUS' || tpaKillswitchValue === 'EASTUS' || tpaKillswitchValue === 'EASTUS2';
  }


  redirectToMemberPaymentResearch(refId: string) {
    const url = new URL(`${APP_CONFIG.SSO_BASE_URL}:${APP_CONFIG.SSO_PRIMARY_SSL_PORT}${APP_CONFIG.SSO_ENDPOINT}`);
    url.searchParams.append(PingParameter.PARTNER_SP_ID, APP_CONFIG.MEMBER_PAYMENT_RESEARCH_PARTNER_ID);
    url.searchParams.append(PingParameter.REF, refId);
    url.searchParams.append(PingParameter.TARGET_RESOURCE, APP_CONFIG.MEMBER_PAYMENT_RESEARCH_TARGET_RESOURCE);
    url.searchParams.append(PingParameter.USER_TYPE, Constants.CSA);
    window.location.href = url.toString();
  }

  redirectToMemberMakePayment(refId: string, preCutoverPageUserFlag: string) {
    const targetResource = (preCutoverPageUserFlag === Constants.Y_FLAG) ? APP_CONFIG.MEMBER_MAKE_A_PAYMENT_FISERV_PRE_CUTOVER_AUTOPAY_TARGET_URL_ID : APP_CONFIG.MEMBER_MAKE_A_PAYMENT_FISERV_TARGET_URL_ID;
    const url = new URL(`${APP_CONFIG.SSO_BASE_URL}:${APP_CONFIG.SSO_PRIMARY_SSL_PORT}${APP_CONFIG.SSO_ENDPOINT}`);
    url.searchParams.append(PingParameter.PARTNER_SP_ID, APP_CONFIG.MEMBER_MAKE_A_PAYMENT_FISERV_PARTNER_SP_ID);
    url.searchParams.append(PingParameter.REF, refId);
    url.searchParams.append(PingParameter.TARGET_RESOURCE, targetResource);
    url.searchParams.append(PingParameter.USER_TYPE, Constants.CSA);
    window.location.href = url.toString();
  }

  redirectToPing(refId: string, partnerSpId: string) {
    const url = new URL(`${APP_CONFIG.SSO_BASE_URL}:${APP_CONFIG.SSO_PRIMARY_SSL_PORT}${APP_CONFIG.SSO_ENDPOINT}`);
    url.searchParams.append(PingParameter.REF, refId);
    url.searchParams.append(PingParameter.PARTNER_SP_ID, partnerSpId);
    window.location.href = url.toString();
  }

  // END - Step 5: Redirect to Ping with Reference ID and required SSO Parameters

  // Redirect to home on any error
  redirectToHome() {
    this.router.navigate(['home']);
  }

  // Update Splunk on drop off success
  updateSplunk() {
    switch (this.applicationName) {
      case SsoApplicationType.MEMBER_PAYMENT_RESEARCH:
        this.updateSplunkOnMemberPaymentResearchSSO();
        break;
      case SsoApplicationType.WHYZEN:
        this.updateSplunkOnWhyzenSSO();
        break;
      case SsoApplicationType.ZIPARI:
        this.updateSplunkOnZipariSSO();
        break;
      case SsoApplicationType.MEMBER:
        this.updateSplunkOnMemberSSO();
        break;
      case SsoApplicationType.SELF_ADMIN:
        this.updateSplunkOnSelfAdminSSO()
        break;
      case SsoApplicationType.PROVIDER:
        this.updateSplunkOnProviderSSO()
        break;
      case SsoApplicationType.PRODUCER:
        this.updateSplunkOnProducerSSO();
        break;
      case SsoApplicationType.EMPLOYER:
        this.updateSplunkOnEmployerSSO();
        break;
      case SsoApplicationType.MEMBER_MAKE_A_PAYMENT:
        this.updateSplunkOnMemberMakeAPaymentSSO();
        break;
      default:
        break;
    }
  }


  // Splunk update for SSO
  updateSplunkOnZipariSSO() {
    this.loggerService.logEvent(
      SSO_SPLUNK.ZIPARI.CODE,
      `${SSO_SPLUNK.ZIPARI.DESCRIPTION} ${this.eventStatus}`,
      SSO_SPLUNK.ZIPARI.ID,
      this.userIdentifierForAudit,
      {
        adminUserId: this.user.getUserIdentifier,
        adminUserName: this.user.getUserName
      },
      this.eventStatus,
      Constants.PROSPECT
    );
  }

  updateSplunkOnMemberSSO() {
    this.loggerService.logEvent(
      SSO_SPLUNK.MEMBER_SPOOF.CODE,
      `${SSO_SPLUNK.MEMBER_SPOOF.DESCRIPTION} ${this.eventStatus}`,
      SSO_SPLUNK.MEMBER_SPOOF.ID,
      this.userIdentifierForAudit,
      {
        adminUserId: this.user.getUserIdentifier,
        adminUserName: this.user.getUserName
      },
      this.eventStatus,
      Constants.MEMBER
    );
  }

  updateSplunkOnMemberMakeAPaymentSSO() {
    this.loggerService.logEvent(
      SSO_SPLUNK.MEMBER_MAKE_A_PAYMENT.CODE,
      `${SSO_SPLUNK.MEMBER_MAKE_A_PAYMENT.DESCRIPTION} ${this.eventStatus}`,
      SSO_SPLUNK.MEMBER_MAKE_A_PAYMENT.ID,
      this.userIdentifierForAudit,
      {
        adminUserId: this.user.getUserIdentifier,
        adminUserName: this.user.getUserName
      },
      this.eventStatus,
      Constants.MEMBER
    );
  }

  updateSplunkOnSelfAdminSSO() {
    this.loggerService.logEvent(
      SSO_SPLUNK.SELF_ADMIN_SPOOF.CODE,
      `${SSO_SPLUNK.SELF_ADMIN_SPOOF.DESCRIPTION} ${this.eventStatus}`,
      SSO_SPLUNK.SELF_ADMIN_SPOOF.ID,
      this.userIdentifierForAudit,
      {
        adminUserId: this.user.getUserIdentifier,
        adminUserName: this.user.getUserName
      },
      this.eventStatus,
      Constants.SELF_FUNDED_PORTAL
    );
  }

  updateSplunkOnMemberPaymentResearchSSO() {
    this.loggerService.logEvent(
      SSO_SPLUNK.MEMBER_PAYMENT_RESEARCH.CODE,
      `${SSO_SPLUNK.MEMBER_PAYMENT_RESEARCH.DESCRIPTION} ${this.eventStatus}`,
      SSO_SPLUNK.MEMBER_PAYMENT_RESEARCH.ID,
      this.userIdentifierForAudit,
      {
        adminUserId: this.user.getUserIdentifier,
        adminUserName: this.user.getUserName
      },
      this.eventStatus,
      Constants.MEMBER
    );
  }

  updateSplunkOnWhyzenSSO() {
    this.loggerService.logEvent(
      SSO_SPLUNK.WHYZEN.CODE,
      `${SSO_SPLUNK.WHYZEN.DESCRIPTION} ${this.eventStatus}`,
      SSO_SPLUNK.WHYZEN.ID,
      this.userIdentifierForAudit,
      {
        adminUserId: this.user.getUserIdentifier,
        adminUserName: this.user.getUserName
      },
      this.eventStatus,
      Constants.EMPLOYER.toUpperCase()
    );
  }


  updateSplunkOnProviderSSO() {
    this.loggerService.logEvent(
      SSO_SPLUNK.PROVIDER.CODE,
      `${SSO_SPLUNK.PROVIDER.DESCRIPTION} ${this.eventStatus}`,
      SSO_SPLUNK.PROVIDER.ID,
      this.userIdentifierForAudit,
      {
        adminUserId: this.user.getUserIdentifier,
        adminUserName: this.user.getUserName
      },
      this.eventStatus,
      Constants.PROVIDER
    );
  }

  updateSplunkOnProducerSSO() {
    this.loggerService.logEvent(
      SSO_SPLUNK.PRODUCER.CODE,
      `${SSO_SPLUNK.PRODUCER.DESCRIPTION} ${this.eventStatus}`,
      SSO_SPLUNK.PRODUCER.ID,
      this.userIdentifierForAudit,
      {
        adminUserId: this.user.getUserIdentifier,
        adminUserName: this.user.getUserName,
        auditMessageText: 'Broker spoof successful for ' + this.bscUserName,
      },
      this.eventStatus,
      Constants.PRODUCER
    );
  }

  updateSplunkOnEmployerSSO() {
    this.loggerService.logEvent(
      SSO_SPLUNK.EMPLOYER.CODE,
      `${SSO_SPLUNK.EMPLOYER.DESCRIPTION} ${this.eventStatus}`,
      SSO_SPLUNK.EMPLOYER.ID,
      this.userIdentifierForAudit,
      {
        adminUserId: this.user.getUserIdentifier,
        adminUserName: this.user.getUserName,
        auditMessageText: 'Employer spoof successful for ' + this.employerUserName,
      },
      this.eventStatus,
      Constants.EMPLOYER.toUpperCase()
    );
  }
  // Method to unsubscribe from subscriptions on component destruction
  ngOnDestroy(): void {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }
}
